<template>
  <div
    class="tw-overlay"
    role="presentation"
    @click.self="onSkip"
  >
    <div
      ref="dialogEl"
      class="tw-dialog"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      :aria-describedby="bodyId"
      tabindex="-1"
      @keydown="onKeydown"
    >
      <button
        type="button"
        class="tw-close"
        aria-label="Skip for now"
        @click="onSkip"
      >
        &times;
      </button>

      <p v-if="screen.kicker" class="tw-kicker">{{ screen.kicker }}</p>
      <h2 :id="titleId" class="tw-title">{{ screen.title }}</h2>
      <p v-if="screen.body" :id="bodyId" class="tw-body">{{ screen.body }}</p>
      <p v-else :id="bodyId" class="sr-only">Choose what you want to explore first.</p>

      <div
        v-if="screen.showInterest"
        class="tw-interests"
        role="radiogroup"
        aria-label="What brings you in"
      >
        <button
          v-for="opt in interestOptions"
          :key="opt.id"
          type="button"
          class="tw-interest"
          role="radio"
          :aria-checked="interest === opt.id"
          :class="{ 'tw-interest--on': interest === opt.id }"
          @click="selectInterest(opt.id)"
        >
          {{ opt.label }}
        </button>
      </div>

      <div class="tw-actions">
        <button
          v-if="step < 3"
          ref="primaryBtn"
          type="button"
          class="tw-btn tw-btn--primary"
          @click="next"
        >
          Continue
        </button>
        <button
          v-else
          ref="primaryBtn"
          type="button"
          class="tw-btn tw-btn--primary"
          @click="onExplore"
        >
          Explore BarbershopTalk
        </button>
        <button type="button" class="tw-btn tw-btn--ghost" @click="onSkip">
          Skip for now
        </button>
      </div>

      <div class="tw-dots" aria-hidden="true">
        <span
          v-for="n in 3"
          :key="n"
          class="tw-dot"
          :class="{ 'tw-dot--on': step === n }"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useTutorialProgress } from '@/composables/useTutorialProgress';
import { WELCOME_SCREENS, INTEREST_LABELS } from '@/constants/tutorialConfig';

export default {
  name: 'TutorialWelcome',
  setup() {
    const tutorial = useTutorialProgress();
    const dialogEl = ref(null);
    const primaryBtn = ref(null);
    const previousFocus = ref(null);
    const titleId = 'tutorial-welcome-title';
    const bodyId = 'tutorial-welcome-body';

    const step = computed(() => tutorial.state.welcomeScreen);
    const screen = computed(() => WELCOME_SCREENS[step.value - 1] || WELCOME_SCREENS[0]);
    const interest = computed(() => tutorial.state.interest);

    function focusTrap(e) {
      if (e.key !== 'Tab' || !dialogEl.value) return;
      const focusable = dialogEl.value.querySelectorAll(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const list = Array.from(focusable);
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onSkip();
        return;
      }
      focusTrap(e);
    }

    function next() {
      if (tutorial.state.welcomeScreen < 3) {
        tutorial.state.welcomeScreen += 1;
      }
    }

    function selectInterest(id) {
      tutorial.setInterest(id);
    }

    function onSkip() {
      tutorial.skipWelcome();
    }

    function onExplore() {
      tutorial.completeWelcome();
    }

    onMounted(() => {
      previousFocus.value = document.activeElement;
      nextTick(() => {
        if (primaryBtn.value) primaryBtn.value.focus();
        else if (dialogEl.value) dialogEl.value.focus();
      });
    });

    onBeforeUnmount(() => {
      const el = previousFocus.value;
      if (el && typeof el.focus === 'function') {
        try { el.focus(); } catch { /* ignore */ }
      }
    });

    watch(step, () => {
      nextTick(() => {
        if (primaryBtn.value) primaryBtn.value.focus();
      });
    });

    return {
      dialogEl,
      primaryBtn,
      titleId,
      bodyId,
      step,
      screen,
      interest,
      interestOptions: INTEREST_LABELS,
      next,
      selectInterest,
      onSkip,
      onExplore,
      onKeydown,
    };
  },
};
</script>

<style scoped>
.tw-overlay {
  position: fixed;
  inset: 0;
  z-index: 3300;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 16px;
  padding-bottom: max(16px, env(safe-area-inset-bottom, 0px));
  background:
    radial-gradient(circle at top left, rgba(48, 136, 212, 0.22), transparent 34%),
    radial-gradient(circle at bottom right, rgba(221, 52, 52, 0.16), transparent 30%),
    rgba(2, 6, 23, 0.68);
  backdrop-filter: blur(8px);
  box-sizing: border-box;
  text-align: left;
}

.tw-dialog {
  position: relative;
  width: 100%;
  max-width: 440px;
  max-height: min(88vh, 640px);
  overflow: auto;
  background: #ffffff;
  color: #0f172a;
  border-radius: 18px 18px 14px 14px;
  padding: 28px 24px 22px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.28);
  font-family: 'Titillium Web', sans-serif;
  box-sizing: border-box;
}

.tw-close {
  position: absolute;
  top: 10px;
  right: 12px;
  width: 44px;
  height: 44px;
  border: none;
  background: transparent;
  font-size: 1.6rem;
  line-height: 1;
  color: #64748b;
  cursor: pointer;
  border-radius: 8px;
}

.tw-close:focus-visible {
  outline: 3px solid rgba(11, 126, 227, 0.35);
  outline-offset: 2px;
}

.tw-kicker {
  margin: 0 0 8px;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #0b7ee3;
}

.tw-title {
  margin: 0 0 12px;
  font-size: clamp(1.35rem, 4vw, 1.7rem);
  font-weight: 900;
  line-height: 1.2;
  letter-spacing: -0.02em;
  padding-right: 36px;
}

.tw-body {
  margin: 0 0 20px;
  font-size: 0.98rem;
  line-height: 1.55;
  color: #475569;
}

.tw-interests {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 0 20px;
}

.tw-interest {
  text-align: left;
  min-height: 48px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1.5px solid #e2e8f0;
  background: #f8fafc;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
  cursor: pointer;
}

.tw-interest--on {
  border-color: #0b7ee3;
  background: #eff6ff;
  color: #1e40af;
}

.tw-interest:focus-visible {
  outline: 3px solid rgba(11, 126, 227, 0.35);
  outline-offset: 2px;
}

.tw-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.tw-btn {
  min-height: 48px;
  border-radius: 12px;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 800;
  cursor: pointer;
  border: none;
}

.tw-btn--primary {
  background: #0b7ee3;
  color: #ffffff;
}

.tw-btn--ghost {
  background: transparent;
  color: #64748b;
  border: 1.5px solid #e2e8f0;
}

.tw-btn:focus-visible {
  outline: 3px solid rgba(11, 126, 227, 0.35);
  outline-offset: 2px;
}

.tw-dots {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 16px;
}

.tw-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #cbd5e1;
}

.tw-dot--on {
  background: #0b7ee3;
}

@media (min-width: 700px) {
  .tw-overlay {
    align-items: center;
    padding: 24px;
  }
  .tw-dialog {
    border-radius: 18px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tw-overlay,
  .tw-dialog {
    transition: none;
  }
}
</style>
